import { useCallback, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import Popover from './Popover';
import Listbox from './Listbox';
import { useListNavigation } from '../../hooks/useListNavigation';
import { type AnchoredWidth } from '../../hooks/useAnchoredPosition';
import { cx, getOptionId, useUiText } from './primitiveUtils';

/**
 * A custom select that always opens DOWNWARD (a native <select> lets the browser
 * choose, and near the bottom of a card it opens up). See Apps/web/THEME.md.
 *
 * The APG "select-only combobox" pattern: the trigger is `role="combobox"` and keeps
 * focus; the list is a Listbox in a Popover, tracked with `aria-activedescendant`.
 *
 * Closed: ArrowDown/ArrowUp/Enter/Space open it on the chosen option; Home/End open
 * it on the first/last; typing a letter opens it and jumps (Arabic-aware).
 * Open: arrows, Home/End, PageUp/PageDown move; Enter/Space choose; Escape closes;
 * Tab chooses the highlighted option and moves on.
 *
 * Used for the bedrooms / bathrooms cells of the range grid.
 */

export interface SelectMenuOption<V extends string = string> {
  value: V;
  label: string;
  disabled?: boolean;
  /** Optional icon before the label (sized by the menu). */
  icon?: ReactNode;
}

export interface SelectMenuProps<V extends string = string> {
  id?: string;
  /** Accessible name; visible above the trigger unless `hideLabel`. */
  label: string;
  hideLabel?: boolean;
  value: V;
  onChange: (value: V) => void;
  options: readonly SelectMenuOption<V>[];
  /** Shown while no option matches `value`. Default «اختر». */
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** sm: 0.875rem text / 1rem icons. md (default): 0.9375rem / 1.125rem. */
  size?: 'sm' | 'md';
  /** 'field': bordered. 'bare': borderless, fills a cell of a larger control. */
  variant?: 'field' | 'bare';
  leadingIcon?: ReactNode;
  popoverWidth?: AnchoredWidth;
  /** Default '10rem', so a narrow grid cell still gets a readable list. */
  popoverMinWidth?: number | string;
  className?: string;
  'aria-describedby'?: string;
}

const SIZE = {
  sm: { text: 'text-[0.875rem]', icon: 'text-[1rem] [&_svg]:h-4 [&_svg]:w-4', pad: 'px-3 gap-2' },
  md: { text: 'text-[0.9375rem]', icon: 'text-[1.125rem] [&_svg]:h-[1.125rem] [&_svg]:w-[1.125rem]', pad: 'px-3.5 gap-2.5' },
} as const;

export default function SelectMenu<V extends string = string>({
  id: idProp,
  label,
  hideLabel = false,
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  invalid = false,
  size = 'md',
  variant = 'field',
  leadingIcon,
  popoverWidth = 'anchor',
  popoverMinWidth = '10rem',
  className = '',
  'aria-describedby': ariaDescribedBy,
}: SelectMenuProps<V>) {
  const tx = useUiText();
  const generatedId = useId();
  const triggerId = idProp ?? `select-${generatedId}`;
  const listId = `${triggerId}-listbox`;
  const labelId = `${triggerId}-label`;
  const triggerRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const selectedIndex = useMemo(() => options.findIndex((o) => o.value === value), [options, value]);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const choose = useCallback(
    (index: number) => {
      const option = options[index];
      if (!option || option.disabled) return;
      if (option.value !== value) onChange(option.value);
      setOpen(false);
      setActiveIndex(-1);
    },
    [onChange, options, value],
  );

  const nav = useListNavigation({
    count: options.length,
    activeIndex,
    onActiveIndexChange: setActiveIndex,
    isDisabled: (i) => !!options[i]?.disabled,
    getLabel: (i) => options[i]?.label ?? '',
    onSelect: choose,
    selectOnSpace: true,
  });

  const openAt = (index: number) => {
    if (disabled) return;
    setOpen(true);
    setActiveIndex(index);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled || event.nativeEvent.isComposing) return;
    const { key } = event;

    if (!open) {
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
        event.preventDefault();
        openAt(selectedIndex >= 0 ? selectedIndex : nav.firstEnabled());
      } else if (key === 'Home') {
        event.preventDefault();
        openAt(nav.firstEnabled());
      } else if (key === 'End') {
        event.preventDefault();
        openAt(nav.lastEnabled());
      } else if (key.length === 1 && key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        setOpen(true);
        const hit = nav.typeahead(key);
        if (hit < 0) setActiveIndex(selectedIndex >= 0 ? selectedIndex : nav.firstEnabled());
      }
      return;
    }

    if (key === 'Tab') {
      // APG: Tab commits the highlighted option, then focus moves on as usual.
      if (activeIndex >= 0) choose(activeIndex);
      else setOpen(false);
      return;
    }
    if (key === 'ArrowUp' && event.altKey) {
      event.preventDefault();
      if (activeIndex >= 0) choose(activeIndex);
      else setOpen(false);
      return;
    }
    nav.handleKeyDown(event);
  };

  const s = SIZE[size];
  const display = selected ? selected.label : placeholder ?? tx('select.placeholder', 'Select');

  return (
    <div className={cx('relative min-w-0', className)}>
      <span
        id={labelId}
        className={hideLabel ? 'sr-only' : 'mb-1.5 block text-[0.875rem] font-medium text-[var(--pv-color-text)]'}
        onClick={() => triggerRef.current?.focus()}
      >
        {label}
      </span>
      <div
        ref={triggerRef}
        id={triggerId}
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-labelledby={labelId}
        aria-activedescendant={open && activeIndex >= 0 ? getOptionId(listId, activeIndex) : undefined}
        aria-disabled={disabled || undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={ariaDescribedBy}
        onClick={() => {
          if (disabled) return;
          if (open) setOpen(false);
          else openAt(selectedIndex >= 0 ? selectedIndex : nav.firstEnabled());
        }}
        onKeyDown={onKeyDown}
        className={cx(
          'flex min-h-[2.75rem] w-full cursor-pointer select-none items-center text-start outline-none',
          s.pad,
          s.text,
          variant === 'field'
            ? cx(
                'rounded-xl border bg-[var(--pv-color-surface-strong)] transition-colors duration-200 dark:bg-[#08372F]',
                invalid
                  ? 'border-[#DF2F2B] focus-visible:ring-2 focus-visible:ring-[#DF2F2B]/20'
                  : 'border-[var(--pv-border-soft)] hover:border-[var(--pv-color-accent-soft)] focus-visible:border-[#004D40] focus-visible:ring-2 focus-visible:ring-[#004D40]/20 dark:focus-visible:border-[#3FBBA2] dark:focus-visible:ring-[#3FBBA2]/25',
                open && !invalid && 'border-[#004D40] dark:border-[#3FBBA2]',
              )
            : 'h-full rounded-[inherit] bg-transparent focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#004D40]/20',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        {leadingIcon && (
          <span className={cx('shrink-0 leading-none text-[var(--pv-color-primary)]', s.icon)} aria-hidden="true">
            {leadingIcon}
          </span>
        )}
        <span
          className={cx(
            'min-w-0 flex-1 truncate',
            selected ? 'text-[var(--pv-color-text)]' : 'text-[var(--pv-color-text-muted)]',
          )}
        >
          {display}
        </span>
        <i
          className={cx(
            'ri-arrow-down-s-line shrink-0 leading-none text-[var(--pv-color-text-muted)] transition-transform duration-200',
            s.icon,
            open && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </div>

      <Popover
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            setOpen(false);
            setActiveIndex(-1);
          }
        }}
        anchorRef={triggerRef}
        width={popoverWidth}
        minWidth={popoverMinWidth}
      >
        <Listbox
          id={listId}
          options={options}
          getOptionKey={(o) => o.value || '__empty'}
          renderOption={(o) =>
            o.icon ? (
              <span className="flex items-center gap-2.5">
                <span className={cx('shrink-0 leading-none text-[var(--pv-color-primary)]', s.icon)} aria-hidden="true">
                  {o.icon}
                </span>
                <span className="min-w-0">{o.label}</span>
              </span>
            ) : (
              o.label
            )
          }
          activeIndex={activeIndex}
          onActiveIndexChange={setActiveIndex}
          onSelect={(_, index) => choose(index)}
          isSelected={(o) => o.value === value}
          isDisabled={(o) => !!o.disabled}
          aria-labelledby={labelId}
          size={size}
        />
      </Popover>
    </div>
  );
}
